import type { LucideIcon } from 'lucide-react';
import {
  UserPlus, Users, FileText, CheckSquare, Folder, Mail,
  MessageCircle, Workflow, Package, Receipt, Sparkles,
} from 'lucide-react';

export type SectionKey =
  | 'leads' | 'clients' | 'deals' | 'tasks' | 'projects'
  | 'newsletter' | 'whatsapp' | 'workflows' | 'warehouse' | 'invoices';

export interface HintCopy {
  badge: string;
  title: string;       // {count} sarà rimpiazzato a runtime
  body: string;
  cta?: { label: string; href: string };
  secondaryCta?: { label: string; href: string };
  icon: LucideIcon;
  accentColor?: string;
}

export interface SectionHintConfig {
  section: SectionKey;
  discoveryThreshold: number;
  discovery: HintCopy;
  advanced: HintCopy;
}

export const SECTION_DISCOVERY_HINTS: SectionHintConfig[] = [
  {
    section: 'leads',
    discoveryThreshold: 10,
    discovery: {
      badge: 'Inizia da qui', icon: UserPlus,
      title: 'I lead sono il tuo carburante',
      body: 'Aggiungi il primo a mano, importa da CSV o connetti Meta Ads per riceverli automaticamente.',
      cta: { label: 'Crea il primo lead', href: '/leads?create=true' },
      secondaryCta: { label: 'Importa da CSV', href: '/settings?tab=import-export' },
    },
    advanced: {
      badge: 'Suggerimento', icon: Sparkles,
      title: 'Hai {count} lead, sai che puoi anche...',
      body: 'Configurare regole di assegnazione round-robin al team, oppure ricevere lead direttamente da Meta Ads via webhook, o usare WhatsApp per il primo contatto automatico.',
      cta: { label: 'Configura assegnazione', href: '/settings?tab=clients-lead-auto-assignment' },
    },
  },
  {
    section: 'clients',
    discoveryThreshold: 10,
    discovery: {
      badge: 'Inizia da qui', icon: Users,
      title: 'I clienti sono i tuoi rapporti consolidati',
      body: 'Trasforma un lead qualificato in cliente o aggiungilo manualmente. Tutto lo storico interazioni resta a portata di mano.',
      cta: { label: 'Aggiungi cliente', href: '/clients?create=true' },
    },
    advanced: {
      badge: 'Suggerimento', icon: Sparkles,
      title: 'Hai {count} clienti, esplora di più',
      body: 'Usa le note per registrare ogni interazione e i tag per segmentare per priorità o tipologia.',
    },
  },
  {
    section: 'deals',
    discoveryThreshold: 5,
    discovery: {
      badge: 'Inizia da qui', icon: FileText,
      title: 'Le offerte sono le tue trattative di vendita',
      body: 'Creale da un lead qualificato o aggiungile a mano, poi falle avanzare negli stati della tua pipeline.',
      cta: { label: "Crea un'offerta", href: '/deals?create=true' },
      secondaryCta: { label: 'Vedi la pipeline', href: '/deals' },
    },
    advanced: {
      badge: 'Suggerimento', icon: Sparkles,
      title: 'Hai {count} offerte aperte, ottimo lavoro',
      body: 'Configura i template di preventivo per chiudere più veloce o imposta gli obiettivi di vendita per capire come sta andando il mese.',
      cta: { label: 'Imposta obiettivi', href: '/settings?tab=sales-targets' },
    },
  },
  {
    section: 'tasks',
    discoveryThreshold: 10,
    discovery: {
      badge: 'Inizia da qui', icon: CheckSquare,
      title: 'I task organizzano il tuo lavoro',
      body: 'Crea attività collegate a clienti, offerte o progetti. Tracciale fino al completamento.',
      cta: { label: 'Nuovo task', href: '/my-tasks' },
    },
    advanced: {
      badge: 'Suggerimento', icon: Sparkles,
      title: '{count} task gestiti, fai di più',
      body: 'Usa le scadenze ricorrenti per le attività ripetitive e assegnale ai colleghi con notifica automatica.',
    },
  },
  {
    section: 'projects',
    discoveryThreshold: 3,
    discovery: {
      badge: 'Inizia da qui', icon: Folder,
      title: 'I progetti raggruppano lavoro complesso',
      body: 'Crea un progetto per ogni cliente strategico. Aggiungi task, documenti e milestone in un unico posto.',
      cta: { label: 'Nuovo progetto', href: '/projects?create=true' },
    },
    advanced: {
      badge: 'Suggerimento', icon: Sparkles,
      title: '{count} progetti attivi',
      body: "Esplora le health card per monitorare lo stato dei progetti a colpo d'occhio.",
    },
  },
  {
    section: 'newsletter',
    discoveryThreshold: 1,
    discovery: {
      badge: 'Inizia da qui', icon: Mail,
      title: 'Comunica via email coi tuoi contatti',
      body: 'Crea liste segmentate, lancia campagne, traccia aperture e click.',
      cta: { label: 'Crea una lista', href: '/newsletter?tab=lists' },
    },
    advanced: {
      badge: 'Suggerimento', icon: Sparkles,
      title: '{count} campagne inviate',
      body: 'Analizza i tassi di apertura per segmento e ottimizza i subject in base ai click.',
    },
  },
  {
    section: 'whatsapp',
    discoveryThreshold: 50,
    discovery: {
      badge: 'Inizia da qui', icon: MessageCircle,
      title: 'WhatsApp dentro al CRM',
      body: 'Connetti il tuo numero e rispondi ai lead da Flusia. Le chat restano collegate ai clienti.',
      cta: { label: 'Configura WhatsApp', href: '/settings?tab=whatsapp-config' },
    },
    advanced: {
      badge: 'Suggerimento', icon: Sparkles,
      title: 'Hai già {count} messaggi',
      body: 'Configura i template per le risposte rapide e i tag per categorizzare le chat per priorità.',
    },
  },
  {
    section: 'workflows',
    discoveryThreshold: 3,
    discovery: {
      badge: 'Inizia da qui', icon: Workflow,
      title: 'Automatizza i task ripetitivi',
      body: 'Crea workflow per assegnare automaticamente lead, inviare email di follow-up, e altro.',
      cta: { label: 'Nuovo workflow', href: '/automation' },
    },
    advanced: {
      badge: 'Suggerimento', icon: Sparkles,
      title: '{count} workflow attivi',
      body: 'Usa la generazione con AI per trasformare i lead in offerte in base ai criteri che imposti.',
    },
  },
  {
    section: 'warehouse',
    discoveryThreshold: 10,
    discovery: {
      badge: 'Inizia da qui', icon: Package,
      title: 'Gestisci scorte e movimenti',
      body: 'Aggiungi prodotti, traccia carico/scarico, collega DDT ai clienti.',
      cta: { label: 'Aggiungi prodotto', href: '/magazzino/prodotti/nuovo' },
    },
    advanced: {
      badge: 'Suggerimento', icon: Sparkles,
      title: '{count} prodotti in magazzino',
      body: 'Configura riassortimento automatico per i prodotti sotto scorta minima.',
    },
  },
  {
    section: 'invoices',
    discoveryThreshold: 5,
    discovery: {
      badge: 'Inizia da qui', icon: Receipt,
      title: 'Fatturazione integrata',
      body: 'Collega Fatture in Cloud ed emetti le fatture direttamente dalle offerte vinte.',
      cta: { label: 'Configura FIC', href: '/settings?tab=fattureincloud' },
    },
    advanced: {
      badge: 'Suggerimento', icon: Sparkles,
      title: '{count} fatture emesse',
      body: 'Usa la coda fatture per programmare emissioni ricorrenti per i clienti in abbonamento.',
    },
  },
];
